import {useEffect, MutableRefObject} from 'react';
import {Platform} from 'react-native';
import {useAppDispatch} from '../../../redux';
import {logoutEsiaSession} from '../../../redux/actions/login';
import {assertSamePerson, showEsiaMismatchAlert} from '../../../redux/actions/cardReissue';
import {loginSlice} from '../../../redux/reducers/login';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {storage, StorageKeys} from '../../../shared/storage';
import {
  loadCardReissueExpectedSnils,
  clearCardReissueEsiaPending,
  isCardReissueEsiaPending,
} from '../../../shared/storage/cardReissueEsia';
import {shouldActivateCardFromEsia} from '../../login/cardActivation/decision';

type Params = {
  isFocused: boolean;
  isDesktop: boolean;
  isAuthorized: boolean;
  esiaRegInfo: AuthorizeEsiaResponsePayload | null;
  hideAndLockGlobalLoader: () => void;
  openActivation: () => void;
  openForm: (readOnly?: boolean) => void;
  prepareRegCardState: (regInfo: AuthorizeEsiaResponsePayload) => void;
  handledEsiaTokenRef: MutableRefObject<string | null>;
  activationOpenedRef: MutableRefObject<boolean>;
  setEsiaLogoutLink: (link: string | null) => void;
};

export const useReissueReturnFromEsia = ({
  isFocused,
  isDesktop,
  isAuthorized,
  esiaRegInfo,
  hideAndLockGlobalLoader,
  openActivation,
  openForm,
  prepareRegCardState,
  handledEsiaTokenRef,
  activationOpenedRef,
  setEsiaLogoutLink,
}: Params) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isFocused || !esiaRegInfo) return;
    if (shouldActivateCardFromEsia(esiaRegInfo)) return;
    if (isAuthorized && !isDesktop && Platform.OS !== 'web') return;

    const handledKey = esiaRegInfo.token ?? esiaRegInfo.snils ?? 'esia-reissue-return';
    if (handledEsiaTokenRef.current === handledKey) return;

    const openReissueFormIfIdentityMatches = async () => {
      const expectedSnils = await loadCardReissueExpectedSnils();
      const isReissueEsiaReturn = isCardReissueEsiaPending() || Boolean(expectedSnils);
      if (!isReissueEsiaReturn) return;

      handledEsiaTokenRef.current = handledKey;
      const matches = await dispatch(assertSamePerson(esiaRegInfo, expectedSnils));

      if (!matches) {
        dispatch(showEsiaMismatchAlert());
        dispatch(loginSlice.actions.setEsiaRegistrationInfo(null));
        await storage.removeItem(StorageKeys.activationToken);
        await clearCardReissueEsiaPending();
        const logoutLink = (await dispatch(logoutEsiaSession())) as string | null;
        if (logoutLink) {
          setEsiaLogoutLink(logoutLink);
        }
        hideAndLockGlobalLoader();
        return;
      }
      prepareRegCardState(esiaRegInfo);
      dispatch(loginSlice.actions.setEsiaCode(null));
      await clearCardReissueEsiaPending();
      openForm();
    };

    openReissueFormIfIdentityMatches().catch(() => undefined);
  }, [
    hideAndLockGlobalLoader,
    dispatch,
    esiaRegInfo,
    handledEsiaTokenRef,
    isAuthorized,
    isDesktop,
    isFocused,
    openForm,
    prepareRegCardState,
    setEsiaLogoutLink,
  ]);

  useEffect(() => {
    if (!isFocused) {
      activationOpenedRef.current = false;
      return;
    }
    if (!shouldActivateCardFromEsia(esiaRegInfo) || activationOpenedRef.current) return;

    const openIfReissueReturn = async () => {
      const isReissueEsiaReturn = isCardReissueEsiaPending() || Boolean(await loadCardReissueExpectedSnils());
      if (!isReissueEsiaReturn) return;
      activationOpenedRef.current = true;
      openActivation();
    };

    openIfReissueReturn().catch(() => undefined);
  }, [activationOpenedRef, esiaRegInfo, isFocused, openActivation]);
};
